import React, { useCallback, useEffect, useState } from 'react';
import './Management.css';
import ManagementTableSkeleton from './Components/ManagementTableSkeleton';
import {
   Table, Space, Tooltip, Button, Input, InputNumber, Select, Tag,
   Modal, Typography, Spin, Popconfirm, Empty, Form, message
} from 'antd';
import ResponsiveBreadcrumb from '../../components/breadcrumb/ResponsiveBreadcrumb';
import {
   SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
   ReloadOutlined, CodeOutlined, CopyOutlined, RobotOutlined, LockOutlined
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const AI_TOPIC_RESTRICTION_FIELD = 'AI_GUARDRAILS_ENABLED';

const getConfigFieldLabel = (cfield) => {
   if (cfield === AI_TOPIC_RESTRICTION_FIELD)
      return 'Limit Brainee Bot to LMS & Teaching Topics';

   return cfield;
}

const CONFIG_ENDPOINTS = {
   list: `${process.env.REACT_APP_API_MANAGEMENT}/getconfigsettings`,
   save: `${process.env.REACT_APP_API_MANAGEMENT}/upsertconfigsetting`,
   remove: `${process.env.REACT_APP_API_MANAGEMENT}/removeconfigsetting`,
   aiProvider: `${process.env.REACT_APP_API_MANAGEMENT}/aiprovidersettings`,
};

const formatDate = (value) => {
   if (!value)
      return '';

   return value;
}

const isJsonValue = (value) => {
   if (value === null || value === undefined)
      return false;

   const trimmed = value.toString().trim();
   return trimmed.startsWith('{') || trimmed.startsWith('[');
}

const isValidJson = (value) => {
   if (!isJsonValue(value))
      return false;

   try {
      JSON.parse(value);
      return true;
   } catch (error) {
      return false;
   }
}

const getAutoCorrectedJson = (value) => {
   const trimmedValue = value.toString().trim();

   try {
      return {
         data: JSON.parse(trimmedValue),
         corrected: false,
      };
   } catch (error) {
      const correctedValue = trimmedValue
         .replace(/[“”]/g, '"')
         .replace(/[‘’]/g, "'")
         .replace(/([{,]\s*)([A-Za-z_$][\w$-]*)(\s*:)/g, '$1"$2"$3')
         .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (match, content) => JSON.stringify(content.replace(/\\'/g, "'")))
         .replace(/(:\s*|[\[,]\s*)True\b/g, '$1true')
         .replace(/(:\s*|[\[,]\s*)False\b/g, '$1false')
         .replace(/(:\s*|[\[,]\s*)None\b/g, '$1null')
         .replace(/,\s*([}\]])/g, '$1');

      return {
         data: JSON.parse(correctedValue),
         corrected: correctedValue !== trimmedValue,
      };
   }
}

const getValueType = (value) => {
   if (value === null || value === undefined || value === '')
      return 'Blank';

   const normalized = value.toString().trim().toLowerCase();

   if (normalized === 'yes' || normalized === 'no')
      return 'Yes/No';

   if (!isNaN(Number(normalized)) && normalized !== '')
      return 'Number';

   if (isJsonValue(value))
      return isValidJson(value) ? 'JSON' : 'Invalid JSON';

   return 'Text';
}

const getValueTagColor = (type) => {
   switch (type) {
      case 'JSON':
         return 'blue';
      case 'Invalid JSON':
         return 'red';
      case 'Yes/No':
         return 'green';
      case 'Number':
         return 'purple';
      case 'Blank':
         return 'default';
      default:
         return 'geekblue';
   }
}

const normalizeConfigRows = (payload) => {
   const normalizeRow = (row) => ({
      id: row.id,
      cfield: row.cfield || row.cField || row.CField || '',
      cvalue: row.cvalue || row.cValue || row.CValue || '',
      created_at: row.created_at || row.createdAt || row.CreatedAt || '',
   });

   let rows = [];

   if (Array.isArray(payload))
      rows = payload;

   else if (payload && Array.isArray(payload.rows))
      rows = payload.rows;

   else if (payload && Array.isArray(payload.config_settings))
      rows = payload.config_settings;

   else if (payload && Array.isArray(payload.data))
      rows = payload.data;

   return rows.map(normalizeRow).sort((a, b) => (a.cfield || '').localeCompare(b.cfield || ''));
}

const getErrorContent = (error, fallback) => {
   if (error && error.response && error.response.data)
      return error.response.data;

   if (error && error.message)
      return error.message;

   return fallback;
}

export function ConfigSettings() {
   const navigate = useNavigate();
   const [form] = Form.useForm();

   const [searchText, setSearchText] = useState('');
   const [searchedColumn, setSearchedColumn] = useState('');
   const [configList, setConfigList] = useState([]);
   const [loading, setLoading] = useState(false);
   const [updating, setUpdating] = useState(false);
   const [modalVisible, setModalVisible] = useState(false);
   const [selectedConfig, setSelectedConfig] = useState(null);
   const [fieldValue, setFieldValue] = useState('');
   const [valueInputType, setValueInputType] = useState('Text');
   const [aiSettingsLoading, setAISettingsLoading] = useState(false);
   const [savingAISettings, setSavingAISettings] = useState(false);
   const [aiSettings, setAISettings] = useState({
      provider: 'OPENAI',
      openAIKey: '',
      deepSeekKey: '',
      openAIModel: 'gpt-4.1-2025-04-14',
      deepSeekModel: 'deepseek-v4-flash',
      openAIConfigured: false,
      deepSeekConfigured: false,
   });

   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const normalizedUserRole = String(user_role || '').trim().toLowerCase();
   const isSuperAdmin = normalizedUserRole === 'super admin';

   const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   };

   const handleReset = clearFilters => {
      clearFilters();
      setSearchText('');
   };

   const getColumnSearchProps = dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}
               >
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
            </Space>
         </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#033D89' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   });

   const getConfigSettings = useCallback(() => {
      setLoading(true);

      axios.get(CONFIG_ENDPOINTS.list).then(response => {
         const data = normalizeConfigRows(response.data);
         setConfigList(data);
         setLoading(false);
      }, error => {
         setLoading(false);
         console.log(error.message);
         Modal.error({
            title: 'Configuration Management',
            content: getErrorContent(error, 'Unable to load configuration settings.'),
            centered: true,
         });
      });
   }, []);

   const getAIProviderSettings = useCallback(() => {
      setAISettingsLoading(true);
      axios.get(CONFIG_ENDPOINTS.aiProvider).then(response => {
         setAISettings(current => ({
            ...current,
            ...response.data,
            openAIKey: '',
            deepSeekKey: '',
         }));
         setAISettingsLoading(false);
      }, error => {
         setAISettingsLoading(false);
         Modal.error({
            title: 'AI Provider Settings',
            content: getErrorContent(error, 'Unable to load AI provider settings.'),
            centered: true,
         });
      });
   }, []);

   useEffect(() => {
      Utils.verifyJwt();

      const allowedRoles = ['super admin', 'admin'];
      if (!allowedRoles.includes(normalizedUserRole)) {
         navigate('/home');
         return;
      }

      getConfigSettings();
      if (isSuperAdmin)
         getAIProviderSettings();
   }, [getAIProviderSettings, getConfigSettings, isSuperAdmin, navigate, normalizedUserRole]);

   const updateAISetting = (field, value) => {
      setAISettings(current => ({ ...current, [field]: value }));
   }

   const handleSaveAISettings = () => {
      setSavingAISettings(true);
      axios.post(CONFIG_ENDPOINTS.aiProvider, {
         provider: aiSettings.provider,
         openAIKey: aiSettings.openAIKey,
         deepSeekKey: aiSettings.deepSeekKey,
         openAIModel: aiSettings.openAIModel,
         deepSeekModel: aiSettings.deepSeekModel,
      }).then(response => {
         setSavingAISettings(false);
         message.success(response.data?.message || 'AI provider settings saved.');
         getAIProviderSettings();
      }, error => {
         setSavingAISettings(false);
         Modal.error({
            title: 'AI Provider Settings',
            content: getErrorContent(error, 'Unable to save AI provider settings.'),
            centered: true,
         });
      });
   }

   const handleAdd = () => {
      setSelectedConfig(null);
      setFieldValue('');
      setValueInputType('Text');
      form.resetFields();
      setModalVisible(true);
   }

   const handleEdit = (record) => {
      const configValue = record.cvalue || '';
      const normalizedValue = getValueType(configValue) === 'Yes/No' ? configValue.toString().trim().toLowerCase() : configValue;
      const normalizedType = getValueType(normalizedValue);

      setSelectedConfig(record);
      setFieldValue(normalizedValue);
      setValueInputType(normalizedType === 'Invalid JSON' ? 'JSON' : normalizedType);
      form.setFieldsValue({
         cfield: record.cfield,
         cvalue: normalizedValue,
      });
      setModalVisible(true);
   }

   const handleCancel = () => {
      setModalVisible(false);
      setSelectedConfig(null);
      setFieldValue('');
      setValueInputType('Text');
      form.resetFields();
   }

   const handleFormatJson = () => {
      const trimmedValue = fieldValue ? fieldValue.toString().trim() : '';

      if (!trimmedValue) {
         message.info('Enter a JSON value before formatting.');
         return;
      }

      try {
         const parsedValue = getAutoCorrectedJson(trimmedValue);
         const formatted = JSON.stringify(parsedValue.data, null, 2);
         setFieldValue(formatted);
         form.setFieldsValue({ cvalue: formatted });
         message.success(parsedValue.corrected ? 'JSON auto-corrected and formatted.' : 'JSON formatted.');
      } catch (error) {
         Modal.error({
            title: 'Configuration Management',
            content: 'The value could not be auto-corrected. Please check the JSON syntax.',
            centered: true,
         });
      }
   }

   const handleCopyValue = (value) => {
      if (!value)
         return;

      navigator.clipboard.writeText(value);
      message.info('Copied to clipboard.');
   }

   const syncConfigValue = (value) => {
      const normalizedValue = value === undefined || value === null ? '' : value.toString();

      setFieldValue(normalizedValue);
      form.setFieldsValue({ cvalue: normalizedValue });
   }

   const handleValueTypeChange = (type) => {
      setValueInputType(type);
      syncConfigValue('');
   }

   const getActiveValueType = () => {
      if (!selectedConfig)
         return valueInputType;

      const valueType = getValueType(fieldValue);
      return valueType === 'Blank' ? valueInputType : valueType;
   }

   const isJsonEditorActive = () => {
      const activeValueType = getActiveValueType();
      return activeValueType === 'JSON' || activeValueType === 'Invalid JSON';
   }

   const renderValueInput = () => {
      const valueType = getActiveValueType();

      if (selectedConfig?.cfield === AI_TOPIC_RESTRICTION_FIELD) {
         return (
            <Select
               className="management-config-setting-control"
               size="middle"
               options={[
                  { value: 'yes', label: 'Enabled' },
                  { value: 'no', label: 'Disabled' },
               ]}
               placeholder="Select status"
               onChange={syncConfigValue}
            />
         );
      }

      if (valueType === 'Yes/No') {
         return (
            <Select
               className="management-config-setting-control"
               size="middle"
               options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
               ]}
               placeholder="Select value"
               onChange={syncConfigValue}
            />
         );
      }

      if (valueType === 'Number') {
         return (
            <InputNumber
               className="management-config-setting-control management-config-setting-number"
               size="middle"
               stringMode
               placeholder="Enter number"
               onChange={syncConfigValue}
            />
         );
      }

      if (valueType === 'JSON' || valueType === 'Invalid JSON') {
         return (
            <TextArea
               className="management-config-setting-textarea"
               rows={6}
               autoComplete="off"
               style={{ fontFamily: 'monospace' }}
               placeholder="Enter JSON value"
               onChange={(event) => syncConfigValue(event.target.value)}
            />
         );
      }

      return (
         <Input
            className="management-config-setting-control"
            allowClear
            size="middle"
            autoComplete="off"
            placeholder="Enter configuration value"
            onChange={(event) => syncConfigValue(event.target.value)}
         />
      );
   }

   const handleSave = () => {
      form.validateFields().then(values => {
         const trimmedField = values.cfield ? values.cfield.trim() : '';
         const value = values.cvalue === undefined || values.cvalue === null ? '' : values.cvalue.toString();

         if (isJsonValue(value) && !isValidJson(value)) {
            Modal.error({
               title: 'Configuration Management',
               content: 'Please fix the JSON value before saving.',
               centered: true,
            });
            return;
         }

         setUpdating(true);

         const payload = {
            id: selectedConfig ? selectedConfig.id : 0,
            cfield: trimmedField,
            cvalue: value,
         };

         axios.post(CONFIG_ENDPOINTS.save, payload).then(response => {
            if (response.data) {
               setUpdating(false);

               Modal.success({
                  title: 'Configuration Management',
                  content: selectedConfig ? 'Configuration setting updated.' : 'Configuration setting added.',
                  centered: true,
                  onOk: () => {
                     handleCancel();
                     getConfigSettings();
                  },
               });
            }
         }, error => {
            setUpdating(false);
            console.log(error.message);
            Modal.error({
               title: 'Configuration Management',
               content: getErrorContent(error, 'Unable to save configuration setting.'),
               centered: true,
            });
         });
      });
   }

   const handleRemove = (id) => {
      setUpdating(true);

      axios.post(`${CONFIG_ENDPOINTS.remove}?id=${id}`).then(response => {
         if (response.data) {
            setUpdating(false);

            Modal.success({
               title: 'Configuration Management',
               content: 'Configuration setting removed.',
               centered: true,
            });

            getConfigSettings();
         }
      }, error => {
         setUpdating(false);
         console.log(error.message);
         Modal.error({
            title: 'Configuration Management',
            content: getErrorContent(error, 'Unable to remove configuration setting.'),
            centered: true,
         });
      });
   }

   const renderValue = (record) => {
      const value = record.cvalue || '';
      if (record.cfield === AI_TOPIC_RESTRICTION_FIELD)
         return <Tag color={value.toLowerCase() === 'no' ? 'default' : 'green'}>{value.toLowerCase() === 'no' ? 'Disabled' : 'Enabled'}</Tag>;

      const displayValue = isValidJson(value) ? JSON.stringify(JSON.parse(value), null, 2) : value;

      return (
         // <Tooltip placement="topLeft" title={displayValue}>
            <Paragraph
               ellipsis={{ rows: 2 }}
               style={{ marginBottom: 0, maxWidth: '100%' }}
            >
               {displayValue}
            </Paragraph>
         // </Tooltip>
      );
   }

   const columns = [
      {
         title: 'ID',
         dataIndex: 'id',
         key: 'id',
         sorter: (a, b) => a.id - b.id,
         width: '7%',
      },
      {
         title: 'Item',
         dataIndex: 'cfield',
         key: 'cfield',
         sorter: (a, b) => (a.cfield || '').localeCompare(b.cfield || ''),
         ...getColumnSearchProps('cfield'),
         ellipsis: {
            showTitle: false,
         },
          render: cfield => (
             <Tooltip placement="topLeft" title={cfield === AI_TOPIC_RESTRICTION_FIELD ? `${getConfigFieldLabel(cfield)} (${cfield})` : cfield}>
                <Text strong>{getConfigFieldLabel(cfield)}</Text>
            </Tooltip>
         ),
         width: '24%',
      },
      {
         title: 'Type',
         key: 'value_type',
         render: record => {
            const type = getValueType(record.cvalue);
            return <Tag color={getValueTagColor(type)}>{type}</Tag>
         },
         filters: [
            { text: 'Text', value: 'Text' },
            { text: 'JSON', value: 'JSON' },
            { text: 'Invalid JSON', value: 'Invalid JSON' },
            { text: 'Yes/No', value: 'Yes/No' },
            { text: 'Number', value: 'Number' },
            { text: 'Blank', value: 'Blank' },
         ],
         onFilter: (value, record) => getValueType(record.cvalue) === value,
         width: '11%',
      },
      {
         title: 'Value',
         dataIndex: 'cvalue',
         key: 'cvalue',
         ...getColumnSearchProps('cvalue'),
         render: (cvalue, record) => renderValue(record),
         width: '38%',
      },
      {
         title: 'Created',
         dataIndex: 'created_at',
         key: 'created_at',
         sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
         render: created_at => formatDate(created_at),
         width: '12%',
      },
      {
         title: 'Action',
         key: 'action',
         render: record => (
            <Space size="small">
               <Tooltip placement="top" title="Edit Setting">
                  <EditOutlined color="#033D89" style={{ cursor: 'pointer' }} onClick={() => handleEdit(record)} />
               </Tooltip>
               <Tooltip placement="top" title="Copy Value">
                  <CopyOutlined color="#033D89" style={{ cursor: 'pointer' }} onClick={() => handleCopyValue(record.cvalue)} />
               </Tooltip>
               {record.cfield !== AI_TOPIC_RESTRICTION_FIELD && (
                  <Tooltip placement="top" title="Remove Setting">
                     <Popconfirm
                        title="Are you sure you want to remove this setting?"
                        onConfirm={() => handleRemove(record.id)}
                        okText="Yes"
                        cancelText="No"
                        placement="left"
                     >
                        <DeleteOutlined color="#033D89" style={{ cursor: 'pointer' }} />
                     </Popconfirm>
                  </Tooltip>
               )}
            </Space>
         ),
         width: '8%',
      },
   ];

   return (
      <>
         <div className="dashboard-area-all management-page">
            <div className="dashboard-item management-shell">
               <div className="management-breadcrumb">
                  <ResponsiveBreadcrumb
                     items={[
                        { key: 'home', node: <Link to="/home">Home</Link> },
                        { key: 'management', node: 'Management' },
                        { key: 'configuration', node: 'Configuration Settings' },
                     ]}
                  />
               </div>

               <section className="management-hero">
                  <div>
                     <Text className="management-kicker">Management</Text>
                     <h1>Configuration Settings</h1>
                     <p>Maintain application configuration keys and values used by the school workspace.</p>
                     <div className="management-meta">
                        <span><CodeOutlined /> System settings</span>
                        <span><ReloadOutlined /> {loading ? 'Loading records' : `${configList.length} records`}</span>
                     </div>
                  </div>
                  <div className="management-actions">
                  <Space wrap>
                     <Button className="management-create-btn button-shadow" type="primary" size="large" shape="round" onClick={() => handleAdd()}>
                        <PlusOutlined /> Add New Setting
                     </Button>
                     <Button className="management-secondary-btn button-shadow" size="large" shape="round" onClick={() => getConfigSettings()} icon={<ReloadOutlined />}>
                        Refresh
                     </Button>
                  </Space>
                  </div>
               </section>

               {isSuperAdmin && <section className="management-panel management-ai-panel">
                  <div className="management-panel-header management-ai-header">
                     <div>
                        <Text className="management-kicker"><RobotOutlined /> AI services</Text>
                        <h2>AI Provider</h2>
                        <p>Select the AI used by Brainee chat, lesson generation, and assessment tools.</p>
                     </div>
                     <Button
                        type="primary"
                        shape="round"
                        onClick={handleSaveAISettings}
                        loading={savingAISettings}
                        disabled={aiSettingsLoading}
                     >
                        Save AI Settings
                     </Button>
                  </div>

                  <Spin spinning={aiSettingsLoading}>
                     <div className="management-ai-grid">
                        <div className="management-ai-field">
                           <Text strong>Active provider</Text>
                           <Select
                              value={aiSettings.provider}
                              onChange={value => updateAISetting('provider', value)}
                              options={[
                                 { value: 'OPENAI', label: 'OpenAI' },
                                 { value: 'DEEPSEEK', label: 'DeepSeek V4 Flash' },
                              ]}
                           />
                        </div>

                        <div className="management-ai-field">
                           <Space>
                              <Text strong>OpenAI API key</Text>
                              <Tag color={aiSettings.openAIConfigured ? 'green' : 'default'}>
                                 {aiSettings.openAIConfigured ? 'Configured' : 'Not configured'}
                              </Tag>
                           </Space>
                           <Input.Password
                              prefix={<LockOutlined />}
                              value={aiSettings.openAIKey}
                              onChange={event => updateAISetting('openAIKey', event.target.value)}
                              placeholder={aiSettings.openAIConfigured ? 'Leave blank to keep existing key' : 'Enter OpenAI API key'}
                              autoComplete="new-password"
                           />
                        </div>

                        <div className="management-ai-field">
                           <Space>
                              <Text strong>DeepSeek API key</Text>
                              <Tag color={aiSettings.deepSeekConfigured ? 'green' : 'default'}>
                                 {aiSettings.deepSeekConfigured ? 'Configured' : 'Not configured'}
                              </Tag>
                           </Space>
                           <Input.Password
                              prefix={<LockOutlined />}
                              value={aiSettings.deepSeekKey}
                              onChange={event => updateAISetting('deepSeekKey', event.target.value)}
                              placeholder={aiSettings.deepSeekConfigured ? 'Leave blank to keep existing key' : 'Enter DeepSeek API key'}
                              autoComplete="new-password"
                           />
                        </div>

                        <div className="management-ai-field">
                           <Text strong>Selected model</Text>
                           <Input
                              value={aiSettings.provider === 'DEEPSEEK' ? aiSettings.deepSeekModel : aiSettings.openAIModel}
                              onChange={event => updateAISetting(
                                 aiSettings.provider === 'DEEPSEEK' ? 'deepSeekModel' : 'openAIModel',
                                 event.target.value
                              )}
                           />
                        </div>
                     </div>
                  </Spin>
               </section>}

               <section className="management-panel">
                  <div className="management-table-shell">
                     {
                        loading
                           ?
                           <ManagementTableSkeleton />
                           :
                           (configList.length > 0 && !loading)
                              ?
                              <Table
                                 className="management-table"
                                 rowKey={record => record.id || record.cfield}
                                 size="small"
                                 columns={columns}
                                 dataSource={configList}
                                 pagination={false}
                                 scroll={{
                                    y: 'calc(100svh - 13rem)',
                                    x: '1200px',
                                 }}
                                 footer={() => <div className="management-table-footer"><span>Total Records: {configList.length}</span></div>}
                              />
                              :
                              <div className="management-empty-state">
                                 <Empty image="../images/brainee_mascot.png" description="No configuration settings found" />
                              </div>
                     }
                  </div>
               </section>
            </div>
         </div>

         <Modal
            className="management-modal management-config-setting-modal"
            title={selectedConfig ? 'Edit Configuration Setting' : 'Add Configuration Setting'}
            centered
            destroyOnClose={true}
            onCancel={() => { handleCancel(); }}
            open={modalVisible}
            width={720}
            footer={[
               <Button key="cancel" shape="round" onClick={() => handleCancel()} disabled={updating}>
                  Cancel
               </Button>,
               <Button key="save" type="primary" shape="round" onClick={() => handleSave()} loading={updating}>
                  {selectedConfig ? 'Update' : 'Save'}
               </Button>
            ]}
         >
            <Spin spinning={updating} tip="Saving, please wait...">
               <Form
                  form={form}
                  className="management-config-setting-form"
                  layout="vertical"
                  colon={false}
                  autoComplete="off"
               >
                  <div className="management-config-setting-stack">
                     <Form.Item
                        className="management-config-setting-field"
                        name="cfield"
                        label="Configuration Field"
                        rules={[
                           { required: true, message: 'Configuration field is required' },
                           { pattern: /^[A-Z0-9_]+$/, message: 'Use uppercase letters, numbers, and underscores only' },
                        ]}
                     >
                        <Input
                           allowClear
                           size="middle"
                           autoComplete="off"
                           placeholder="Example: SCHOOL_NAME"
                           disabled={selectedConfig !== null}
                           onChange={(event) => form.setFieldsValue({ cfield: event.target.value.toUpperCase().replace(/\s/g, '_') })}
                        />
                     </Form.Item>

                     <section className="management-config-setting-value-section">
                        {!selectedConfig && (
                           <Form.Item
                              className="management-config-setting-type-item"
                              label="Value Type"
                           >
                              <Select
                                 className="management-config-setting-control"
                                 size="middle"
                                 value={valueInputType}
                                 options={[
                                    { value: 'Text', label: 'Text' },
                                    { value: 'Number', label: 'Number' },
                                    { value: 'Yes/No', label: 'Yes/No' },
                                    { value: 'JSON', label: 'JSON' },
                                 ]}
                                 onChange={handleValueTypeChange}
                              />
                           </Form.Item>
                        )}

                        <Form.Item
                           className="management-config-setting-value-item"
                           name="cvalue"
                           label={
                              <span className="management-config-setting-value-label">
                                 Value
                                 <Tag color={getValueTagColor(getActiveValueType())}>{getActiveValueType()}</Tag>
                              </span>
                           }
                           rules={[{ required: false }]}
                        >
                           {renderValueInput()}
                        </Form.Item>

                        <Space className="management-config-setting-tools" wrap>
                           {isJsonEditorActive() && (
                              <Button htmlType="button" shape="round" icon={<CodeOutlined />} disabled={!fieldValue || !fieldValue.toString().trim()} onClick={() => handleFormatJson()}>
                                 Format JSON
                              </Button>
                           )}
                           <Button htmlType="button" shape="round" icon={<CopyOutlined />} disabled={!fieldValue} onClick={() => handleCopyValue(fieldValue)}>
                              Copy Value
                           </Button>
                        </Space>
                     </section>
                  </div>
               </Form>
            </Spin>
         </Modal>
      </>
   );
}
